/**
 * MeetingPointStep - First step in Key Locations guided editor
 *
 * Required step where guide sets meeting point with:
 * - Location search with autocomplete
 * - Manual coordinate entry
 * - Interactive map with draggable pin
 * - Category-specific labels (Trailhead, Put-In, etc.)
 * - Amenity selection (parking, restrooms, etc.)
 * - Description field
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-234 (US-1.20.6-S.2-01: Meeting Point Editor with Search)
 *
 * @component
 */

import React, { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import Map, { Marker, NavigationControl } from 'react-map-gl';
import { MapPin } from 'lucide-react';
import LocationAutocomplete from '../../../search/LocationAutocomplete';
import AmenitySelector from './AmenitySelector';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * Category-specific meeting point labels
 */
const MEETING_POINT_LABELS = {
  39: 'Trailhead', // Hiking/Backpacking
  40: 'Base Camp', // Mountain/Climbing
  41: 'Start Point', // Cycling/Biking
  42: 'Put-In' // Water Adventures
};

/**
 * MeetingPointStep
 *
 * First and required step for setting meeting point location.
 */
const MeetingPointStep = memo(function MeetingPointStep({ meetingPoint = {}, categoryL1Id, onChange }) {
  // Local state for form fields
  const [locationQuery, setLocationQuery] = useState(meetingPoint.address || '');
  const [name, setName] = useState(meetingPoint.name || '');
  const [description, setDescription] = useState(meetingPoint.description || '');
  const [latitude, setLatitude] = useState(meetingPoint.latitude || '');
  const [longitude, setLongitude] = useState(meetingPoint.longitude || '');
  const [amenities, setAmenities] = useState(meetingPoint.amenities || []);
  const [entryMethod, setEntryMethod] = useState(
    meetingPoint.latitude && meetingPoint.longitude ? 'manual' : 'search'
  );

  // Map viewport state
  const [viewState, setViewState] = useState({
    latitude: parseFloat(meetingPoint.latitude) || 39.8283,
    longitude: parseFloat(meetingPoint.longitude) || -98.5795,
    zoom: meetingPoint.latitude && meetingPoint.longitude ? 12 : 4
  });

  const mapboxToken = import.meta.env.VITE_MAPBOX_API_KEY;

  // Get category-specific label
  const meetingPointLabel =
    MEETING_POINT_LABELS[categoryL1Id] || 'Meeting Point';

  // Notify parent of changes
  const notifyChange = useCallback(
    (updates) => {
      onChange({
        name: name || meetingPointLabel,
        address: locationQuery,
        description,
        latitude: parseFloat(latitude) || null,
        longitude: parseFloat(longitude) || null,
        amenities,
        ...updates
      });
    },
    [name, meetingPointLabel, locationQuery, description, latitude, longitude, amenities, onChange]
  );

  // Handle location search selection
  const handleLocationSelect = useCallback(
    (suggestion) => {
      setLocationQuery(suggestion.description || suggestion.display_name);
      setLatitude(suggestion.latitude || suggestion.lat);
      setLongitude(suggestion.longitude || suggestion.lon || suggestion.lng);

      // Auto-populate name if empty
      if (!name) {
        const suggestedName = suggestion.name || suggestion.description || meetingPointLabel;
        setName(suggestedName);
      }

      notifyChange({
        address: suggestion.description || suggestion.display_name,
        latitude: parseFloat(suggestion.latitude || suggestion.lat),
        longitude: parseFloat(suggestion.longitude || suggestion.lon || suggestion.lng),
        name: name || suggestion.name || meetingPointLabel
      });
    },
    [name, meetingPointLabel, notifyChange]
  );

  // Handle manual coordinate change
  const handleManualCoordinateChange = useCallback(() => {
    notifyChange({
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude)
    });
  }, [latitude, longitude, notifyChange]);

  // Validate coordinates
  const isValidLatitude = latitude !== '' && !isNaN(latitude) && latitude >= -90 && latitude <= 90;
  const isValidLongitude = longitude !== '' && !isNaN(longitude) && longitude >= -180 && longitude <= 180;
  const hasValidCoordinates = isValidLatitude && isValidLongitude;

  // Update map viewport when coordinates change
  useEffect(() => {
    if (hasValidCoordinates) {
      setViewState({
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        zoom: 12
      });
    }
  }, [latitude, longitude, hasValidCoordinates]);

  // Handle marker drag
  const handleMarkerDrag = useCallback((_event) => {
    const { lngLat } = event;
    const newLat = lngLat.lat.toFixed(6);
    const newLng = lngLat.lng.toFixed(6);

    setLatitude(newLat);
    setLongitude(newLng);

    notifyChange({
      latitude: parseFloat(newLat),
      longitude: parseFloat(newLng)
    });
  }, [notifyChange]);

  return (
    <div className="meeting-point-step space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Set {meetingPointLabel}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Where will participants meet you to start the experience?
        </p>
      </div>

      {/* Entry Method Toggle */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => setEntryMethod('search')}
          className={`
            px-4 py-2 text-sm font-medium rounded-md transition-colors
            ${
              entryMethod === 'search'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }
          `}
        >
          Search Location
        </button>
        <button
          type="button"
          onClick={() => setEntryMethod('manual')}
          className={`
            px-4 py-2 text-sm font-medium rounded-md transition-colors
            ${
              entryMethod === 'manual'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }
          `}
        >
          Manual Coordinates
        </button>
      </div>

      {/* Location Search */}
      {entryMethod === 'search' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search for location
          </label>
          <LocationAutocomplete
            value={locationQuery}
            onChange={setLocationQuery}
            onSelect={handleLocationSelect}
            placeholder={`Search for ${meetingPointLabel.toLowerCase()}...`}
            country="US"
            className="w-full"
          />
          <p className="mt-2 text-xs text-gray-500">
            Start typing an address, landmark, or place name
          </p>
        </div>
      )}

      {/* Manual Coordinate Entry */}
      {entryMethod === 'manual' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="latitude"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Latitude
            </label>
            <input
              type="number"
              id="latitude"
              step="any"
              min="-90"
              max="90"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              onBlur={handleManualCoordinateChange}
              placeholder="40.7128"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {latitude !== '' && !isValidLatitude && (
              <p className="mt-1 text-xs text-red-600">
                Latitude must be between -90 and 90
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="longitude"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Longitude
            </label>
            <input
              type="number"
              id="longitude"
              step="any"
              min="-180"
              max="180"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              onBlur={handleManualCoordinateChange}
              placeholder="-74.0060"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {longitude !== '' && !isValidLongitude && (
              <p className="mt-1 text-xs text-red-600">
                Longitude must be between -180 and 180
              </p>
            )}
          </div>
        </div>
      )}

      {/* Location Preview */}
      {hasValidCoordinates && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Location set
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {latitude}, {longitude}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Name */}
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          {meetingPointLabel} Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(  _e) => {
            setName(_e.target.value);
            notifyChange({ name: _e.target.value });
          }}
          placeholder={`e.g., ${meetingPointLabel} Parking Lot`}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Description <span className="text-gray-400">(optional)</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(  _e) => {
            setDescription(_e.target.value);
            notifyChange({ description: _e.target.value });
          }}
          placeholder="Add helpful details like landmarks, parking info, or what to look for..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Amenities */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Available Amenities <span className="text-gray-400">(optional)</span>
        </label>
        <AmenitySelector
          selected={amenities}
          onChange={(newAmenities) => {
            setAmenities(newAmenities);
            notifyChange({ amenities: newAmenities });
          }}
        />
      </div>

      {/* Interactive Map with Draggable Pin */}
      {hasValidCoordinates && mapboxToken && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Map Preview
            <span className="ml-2 text-xs font-normal text-gray-500">
              (Drag the pin to adjust location)
            </span>
          </label>
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow-sm" style={{ height: '400px' }}>
            <Map
              {...viewState}
              onMove={(evt) => setViewState(evt.viewState)}
              mapboxAccessToken={mapboxToken}
              mapStyle="mapbox://styles/mapbox/outdoors-v12"
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-right" />

              <Marker
                latitude={parseFloat(latitude)}
                longitude={parseFloat(longitude)}
                draggable
                onDragEnd={handleMarkerDrag}
              >
                <div className="relative">
                  <MapPin
                    className="w-8 h-8 text-rose-600"
                    fill="currentColor"
                    strokeWidth={1.5}
                    style={{
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                      cursor: 'grab'
                    }}
                  />
                </div>
              </Marker>
            </Map>
          </div>
          <p className="text-xs text-gray-500 italic">
            Coordinates: {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
          </p>
        </div>
      )}

      {hasValidCoordinates && !mapboxToken && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            Map preview unavailable: Mapbox API key not configured
          </p>
        </div>
      )}

      {/* Validation Message */}
      {!hasValidCoordinates && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <svg
            className="w-5 h-5 text-amber-600 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              Location coordinates required
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Please search for a location or enter coordinates manually to continue.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

MeetingPointStep.propTypes = {
  /**
   * Current meeting point data
   */
  meetingPoint: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    description: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    amenities: PropTypes.array
  }),

  /**
   * L1 Category ID (for label customization)
   */
  categoryL1Id: PropTypes.number.isRequired,

  /**
   * Change handler
   */
  onChange: PropTypes.func.isRequired
};

export default MeetingPointStep;
